/**
 * Largura de coluna da folha de monitoramento (PRD 32) — o default por **tipo** de coluna,
 * mais a normalização do valor para os dois formatos que os call sites exigem.
 *
 * Vive no design system, não em `DsAttendance/utils/` — mesma razão de `sheetRowLimits.ts`
 * (ver seu comentário): quem consome é `AttendanceSheetCard.vue` (`Components`), e a direção
 * de camadas de `.claude/rules/architecture.md` (`Modules -> Components`) proíbe um
 * `Component` importar de um `Module`.
 *
 * POR QUE default por tipo: `table.jss_worksheet` roda com `table-layout: fixed` (ver o CSS
 * de `AttendanceSheetCard.vue`), então na engine `spreadsheet` a largura declarada é
 * MANDATÓRIA — o conteúdo não empurra a coluna. Com o default único de 140px que valia até
 * aqui, nome de médico truncava enquanto hora e checkbox sobravam espaço. A largura de
 * domínio (ex.: 260px no nome do médico) continua sendo do módulo chamador, via
 * `SheetColumn.width`; este mapa só cobre quem não declara nada.
 *
 * POR QUE dois formatos de saída: `SheetColumn.width` é `string` livre e sempre foi
 * escrito das duas maneiras no repo — `'140'` (o antigo default global) e `'140px'`
 * (`DsAttendanceAwaitingAudit.vue`, coluna `conciliation`; `AttendanceSheetCard.stories.ts`).
 * Os dois destinos discordam sobre a unidade:
 *
 * - jspreadsheet CE escreve o valor num `<col width="...">`, atributo HTML que só aceita
 *   número — `'100px'` ali é ignorado silenciosamente e a coluna cai na largura automática
 *   da lib. Daí `toGridWidth`, que devolve SEMPRE `number`.
 * - o `<th>` das tabelas nativas (modo `view` e engine `native`) recebe a largura via
 *   `:style`, onde CSS exige unidade — `'140'` cru é uma declaração inválida e é descartada
 *   pelo browser. Daí `toCssWidth`, que devolve SEMPRE uma string com unidade.
 *
 * Normalizar nos dois sentidos aqui (em vez de padronizar o tipo do campo) mantém os call
 * sites existentes funcionando sem migração e tira do chamador a decisão de unidade.
 */

import type { ColumnType, SheetColumn } from './attendanceSheet.types'

/**
 * Default por tipo, em pixels. Colunas de conteúdo livre (`text`, `status`, `readonly`)
 * ficam nos 140px históricos; hora e número são de conteúdo curto e fixo; `checkbox` só
 * precisa caber o rótulo do cabeçalho em volta do controle.
 */
export const DEFAULT_COLUMN_WIDTH_BY_TYPE: Record<ColumnType, string> = {
  text: '140',
  number: '110',
  date: '130',
  time: '110',
  checkbox: '130',
  status: '140',
  readonly: '140'
}

/** Último recurso: o default global que valia antes desta divisão por tipo. */
const FALLBACK_COLUMN_WIDTH_PX = 140

/** A largura declarada pelo chamador vence o default do tipo — inclusive a de domínio. */
export function resolveColumnWidth(col: SheetColumn): string {
  return col.width ?? DEFAULT_COLUMN_WIDTH_BY_TYPE[col.type] ?? String(FALLBACK_COLUMN_WIDTH_PX)
}

/**
 * Largura para o jspreadsheet: sempre número. `Number.parseInt` cobre `'140'`, `'140px'` e
 * `140`; qualquer coisa que não comece por dígito (incluindo `'auto'`) cai no fallback —
 * `NaN` chegando na lib zera a coluna.
 */
export function toGridWidth(col: SheetColumn): number {
  const parsed = Number.parseInt(String(resolveColumnWidth(col)), 10)
  return Number.isNaN(parsed) ? FALLBACK_COLUMN_WIDTH_PX : parsed
}

/**
 * Largura para o `<th>`: sempre com unidade. Número puro ganha `px`; valor que já traz
 * unidade (`'140px'`, `'20%'`, `'12rem'`) é preservado como está.
 */
export function toCssWidth(col: SheetColumn): string {
  const width = String(resolveColumnWidth(col)).trim()
  return /^-?\d*\.?\d+$/.test(width) ? `${width}px` : width
}
